import { ConfirmModal } from '@sparklayer-io/spark-dash-ui/components/confirm-modal';
import { useForm } from '@tanstack/react-form';
import { Button, Card, Checkbox, Form, Input, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { stringToSlug } from '../../../utils/slug';
import {
  ROLE_PERMISSION_CATEGORIES,
  ROLE_PERMISSION_CATEGORY_LABELS,
  ROLE_PERMISSIONS_CATALOG,
} from '../constants/role-permissions';
import {
  useCreateCustomerRole,
  useCustomerRole,
  useDeleteCustomerRole,
  useUpdateCustomerRole,
} from '../hooks/use-customer-roles';
import { useSalesAgentsAvailability } from '../hooks/use-sales-agent-availability';
import type { CustomerRoleType } from '../types/role';

type RoleFormValues = {
  name: string;
  slug: string;
  type: CustomerRoleType;
  permissions: Record<string, boolean>;
};

export const RoleFormPage = withPageProviders(RoleFormPageContent);

function RoleFormContent({
  defaultValues,
  isCreate,
  slug,
}: {
  defaultValues: RoleFormValues;
  isCreate: boolean;
  slug: string | undefined;
}) {
  const { navigationHandler, notificationHandler } = useHandlers();
  const createRole = useCreateCustomerRole();
  const updateRole = useUpdateCustomerRole(slug ?? '');

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const onError = (err: unknown) => {
        const message = (err as { response?: { status?: number } })?.response?.status;
        if (message === 403) {
          notificationHandler.error(t('errors:forbidden'));
        } else if (message === 409) {
          notificationHandler.error(t('errors:conflict'));
        } else {
          notificationHandler.error();
        }
      };

      const onSuccess = () => {
        notificationHandler.success(t('notifications:saved'));
        navigationHandler.navigate('/customers/roles');
      };

      const permissionsPayload =
        Object.keys(value.permissions).length > 0 ? value.permissions : null;

      if (isCreate) {
        await createRole.mutateAsync(
          {
            name: value.name,
            slug: value.slug,
            type: value.type,
            permissions: permissionsPayload,
          },
          { onError, onSuccess },
        );
      } else if (slug) {
        await updateRole.mutateAsync(
          {
            name: value.name,
            permissions: permissionsPayload,
          },
          { onError, onSuccess },
        );
      }
    },
  });

  return (
    <Form onSubmit={form.handleSubmit}>
      <Card>
        <Card.Header headingLevel="h2">
          {t('features:customers.pages.role_form_page.role_details')}
        </Card.Header>
        <Card.Body>
          <div className="grid w-full gap-3 sm:grid-cols-2">
            <form.Field
              name="name"
              listeners={{
                onChange: ({ value, fieldApi }) => {
                  if (isCreate) {
                    fieldApi.form.setFieldValue('slug', stringToSlug(value));
                  }
                },
              }}
            >
              {(field) => (
                <Input
                  label={t('features:customers.pages.role_form_page.display_name_label')}
                  placeholder={t(
                    'features:customers.pages.role_form_page.display_name_placeholder',
                  )}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
            <form.Field name="slug">
              {(field) => (
                <Input
                  label={t('features:customers.pages.role_form_page.handle_label')}
                  placeholder={t('features:customers.pages.role_form_page.handle_placeholder')}
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={!isCreate}
                />
              )}
            </form.Field>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header headingLevel="h2">
          {t('features:customers.pages.role_form_page.permissions')}
        </Card.Header>
        <Card.Body>
          <Text variant="secondary">
            {t('features:customers.pages.role_form_page.permissions_description')}
          </Text>
          <div className="grid grid-cols-1 gap-4">
            {ROLE_PERMISSION_CATEGORIES.map((cat) => (
              <div key={cat} className="flex flex-col gap-2">
                <Text>{ROLE_PERMISSION_CATEGORY_LABELS[cat]}</Text>
                <div className="flex flex-col gap-2">
                  {ROLE_PERMISSIONS_CATALOG.filter((p) => p.category === cat).map((p) => (
                    <form.Field key={p.key} name={`permissions.${p.key}`}>
                      {(field) => (
                        <Checkbox
                          id={`perm-${p.key}`}
                          label={p.label}
                          description={p.description}
                          checked={field.state.value === true}
                          onChange={() => field.handleChange(!field.state.value)}
                        />
                      )}
                    </form.Field>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          type="button"
          onClick={() => navigationHandler.navigate('/customers/roles')}
        >
          {t('buttons:cancel')}
        </Button>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              type="submit"
              disabled={createRole.isPending || updateRole.isPending || isSubmitting}
            >
              {t('buttons:save')}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </Form>
  );
}

function RoleFormPageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const { slug } = navigationHandler.params();
  const isCreate = slug === undefined || slug === 'create';
  const editSlug = !isCreate && slug ? slug : undefined;

  const { data: role, isLoading, isError, refetch } = useCustomerRole(editSlug);
  const deleteRole = useDeleteCustomerRole();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: salesAgents } = useSalesAgentsAvailability();

  const isRolesEnabled: boolean = salesAgents !== undefined && salesAgents.max !== 0;

  if (!isRolesEnabled) {
    navigationHandler.navigate('/customers/roles');
  }

  const handleDelete = async () => {
    if (!slug) return;
    await deleteRole.mutateAsync(slug, {
      onError: () => {
        notificationHandler.error();
      },
      onSuccess: () => {
        setDeleteModalOpen(false);
        notificationHandler.success(t('notifications:deleted'));
        navigationHandler.navigate('/customers/roles');
      },
    });
  };

  const pageTitle = isCreate
    ? t('features:customers.pages.role_form_page.create_title')
    : t('features:customers.pages.role_form_page.edit_title', {
        name: role?.name ?? '',
      });

  if (!isCreate && !slug) {
    navigationHandler.navigate('/customers/roles');
    return null;
  }

  const secondaryActions =
    !isCreate && role
      ? [
          {
            onClick: () => setDeleteModalOpen(true),
            label: t('buttons:delete'),
            icon: 'delete' as const,
            danger: true,
          },
        ]
      : undefined;

  const createDefaultValues: RoleFormValues = {
    name: '',
    slug: '',
    type: 'sales',
    permissions: {},
  };

  const editDefaultValues: RoleFormValues = role
    ? {
        name: role.name,
        slug: role.slug,
        type: role.type,
        permissions: role.permissions ?? {},
      }
    : createDefaultValues;

  return (
    <Page
      narrow
      title={pageTitle}
      backUrl="/customers/roles"
      secondaryActions={secondaryActions}
      description={t('features:customers.pages.role_form_page.role_details_description')}
    >
      <SparkSuspense
        isLoading={isLoading && !isCreate}
        isError={isError && !isCreate}
        errorMessage={t('errors:generic')}
        onReload={() => void refetch()}
      >
        <RoleFormContent
          defaultValues={isCreate ? createDefaultValues : editDefaultValues}
          isCreate={isCreate}
          slug={slug}
        />

        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={() => void handleDelete()}
          loading={deleteRole.isPending}
          title={t('features:customers.pages.role_form_page.delete_confirm_title')}
          confirmText={t('buttons:delete')}
          message={t('features:customers.pages.role_form_page.delete_confirm_body', {
            name: role?.name ?? '',
          })}
        />
      </SparkSuspense>
    </Page>
  );
}
